import React, { useState } from 'react';
import {
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment, { Moment } from 'moment';
import {
  SMS_USAGE_DATE_FORMAT,
  getSmsUsageBusinessKey,
  getSmsUsageOldestDate,
  getSmsUsageToday,
  needsSmsUsageAttention,
} from 'utils/smsUsage/smsUsage';
import { useSmsUsage } from 'api/queries/useSmsUsage';
import { useUpdateSmsUsageBlock } from 'api/queries/useUpdateSmsUsageBlock';
import { SmsUsageBusiness } from 'types/smsUsage/smsUsage';
import SmsUsageBusinessRow, { SMS_USAGE_BUSINESS_COLUMNS } from './SmsUsageBusinessRow';
import './SmsUsageLimitsTable.scss';

const SmsUsageLimitsTable: React.FC = () => {
  const today = getSmsUsageToday();
  const oldestDate = getSmsUsageOldestDate(today);
  // Until the operator picks a day, follow the live UTC today so the page rolls over at midnight.
  const [pickedDate, setPickedDate] = useState<string | null>(null);
  const date = pickedDate ?? today;
  // Rows the operator opened or closed by hand; the rest follow `needsSmsUsageAttention`.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const { data, isLoading, isError } = useSmsUsage(date);
  const updateBlock = useUpdateSmsUsageBlock();

  const businesses = data?.businesses ?? [];
  const isToday = date === today;
  const canChangeBlocks = isToday && !updateBlock.isLoading;

  const isExpanded = (business: SmsUsageBusiness): boolean =>
    expanded[getSmsUsageBusinessKey(business)] ?? needsSmsUsageAttention(business);

  const handleDateChange = (value: Moment | null) => {
    if (!value?.isValid()) {
      return;
    }

    // Read the clock again: a tab left open past midnight may not have re-rendered yet.
    const currentToday = getSmsUsageToday();
    const picked = value.clone().utc().format(SMS_USAGE_DATE_FORMAT);
    // minDate/maxDate don't stop a typed day outside the retained range, which the backend rejects.
    if (picked < getSmsUsageOldestDate(currentToday) || picked > currentToday) {
      return;
    }

    // Picking today goes back to following the live day, so it still rolls over at midnight.
    setPickedDate(picked === currentToday ? null : picked);
    setExpanded({});
  };

  const renderStatusRow = (content: React.ReactNode) => (
    <TableRow>
      <TableCell colSpan={SMS_USAGE_BUSINESS_COLUMNS} align="center">
        {content}
      </TableCell>
    </TableRow>
  );

  const renderBody = () => {
    if (isLoading) {
      return renderStatusRow(<CircularProgress size={24} />);
    }

    if (isError) {
      return renderStatusRow(<Alert severity="error">Failed to load SMS usage.</Alert>);
    }

    if (!businesses.length) {
      return renderStatusRow('No SMS sent on this day');
    }

    return businesses.map((business) => {
      const key = getSmsUsageBusinessKey(business);

      return (
        <SmsUsageBusinessRow
          key={key}
          business={business}
          isExpanded={isExpanded(business)}
          onToggleExpanded={() =>
            setExpanded((current) => ({ ...current, [key]: !isExpanded(business) }))
          }
          canChangeBlocks={canChangeBlocks}
          onBlockChange={updateBlock.mutate}
        />
      );
    });
  };

  return (
    <div className="sms-usage-limits">
      <div className="sms-usage-toolbar">
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DatePicker
            className="sms-usage-date-picker"
            label="Date (UTC)"
            timezone="UTC"
            value={moment.utc(date, SMS_USAGE_DATE_FORMAT)}
            minDate={moment.utc(oldestDate, SMS_USAGE_DATE_FORMAT)}
            maxDate={moment.utc(today, SMS_USAGE_DATE_FORMAT)}
            onChange={handleDateChange}
            slotProps={{ textField: { size: 'small' } }}
          />
        </LocalizationProvider>
      </div>
      <TableContainer component={Paper} className="sms-usage-table">
        <Table aria-label="SMS usage by business">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Business</TableCell>
              <TableCell align="right">Sent</TableCell>
              <TableCell align="right">Highest % used</TableCell>
              <TableCell>Block business</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderBody()}</TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default SmsUsageLimitsTable;
